import * as Dialog from '@radix-ui/react-dialog'
import { AlertTriangle, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useDeleteCompany } from '../useCompanies'
import { Button } from '@/components/ui/button'
import type { Company } from '../types'

interface DeleteCompanyDialogProps {
  open: boolean
  onClose: () => void
  company: Company
  /** Path to navigate to after successful deletion */
  redirectAfter?: string
}

export function DeleteCompanyDialog({
  open,
  onClose,
  company,
  redirectAfter,
}: DeleteCompanyDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const deleteCompany = useDeleteCompany()

  async function handleDelete() {
    await deleteCompany.mutateAsync(company.id)
    onClose()
    if (redirectAfter) void navigate(redirectAfter)
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen && !deleteCompany.isPending) onClose()
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="bg-background data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-top-[48%] fixed top-1/2 left-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border shadow-lg">
          {/* Header */}
          <div className="flex items-center justify-between border-b px-6 py-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="text-destructive h-4 w-4" />
              <Dialog.Title className="text-destructive text-sm font-semibold">
                {t('companies.deleteTitle')}
              </Dialog.Title>
            </div>
            <Dialog.Close asChild>
              <button
                className="text-muted-foreground hover:text-foreground rounded p-1 transition-colors disabled:opacity-50"
                disabled={deleteCompany.isPending}
                aria-label={t('actions.close')}
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="px-6 py-4">
            <Dialog.Description className="text-muted-foreground text-sm">
              {t('companies.deleteConfirm', { name: company.nameTh })}
            </Dialog.Description>

            {deleteCompany.error && (
              <div className="border-destructive/40 bg-destructive/10 text-destructive mt-3 rounded-lg border px-4 py-2 text-xs">
                {t('companies.errors.deleteFailed')}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 border-t px-6 py-4">
            <Button variant="outline" onClick={onClose} disabled={deleteCompany.isPending}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                void handleDelete()
              }}
              disabled={deleteCompany.isPending}
              className="min-w-[80px]"
            >
              {deleteCompany.isPending ? (
                <span className="flex items-center gap-1.5">
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  {t('actions.delete')}
                </span>
              ) : (
                t('actions.delete')
              )}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
